import React, { useState } from 'react';
import {
  Box,
  VStack,
  Heading,
  Text,
  SimpleGrid,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  useDisclosure,
  useColorModeValue,
  IconButton,
  HStack,
} from '@chakra-ui/react';
import { FaArrowLeft, FaArrowRight, FaExpand } from 'react-icons/fa';
import { motion } from 'framer-motion';

// Dynamically import all images from the gallery folder
const importAll = (r) => r.keys().map((key) => ({
  src: r(key).default || r(key),
  filename: key.replace('./', ''),
  caption: key.replace('./', '').replace(/\.[^/.]+$/, '').replace(/-/g, ' ')
}));

const galleryImages = importAll(
  require.context('../media/gallery/', false, /\.(png|jpe?g|svg|webp)$/)
);

const Gallery = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedImage, setSelectedImage] = useState(0);
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const purpleColor = useColorModeValue('purple.600', 'purple.300');
  const orangeColor = useColorModeValue('orange.500', 'orange.300');

  const openImage = (index) => {
    setSelectedImage(index);
    onOpen();
  };

  const navigateImage = (direction) => {
    if (direction === 'prev') {
      setSelectedImage((prev) => (prev === 0 ? galleryImages.length - 1 : prev - 1));
    } else {
      setSelectedImage((prev) => (prev === galleryImages.length - 1 ? 0 : prev + 1));
    }
  };

  return (
    <Box minH="100vh" bg={bgColor} mt={10} py={10} px={{ base: 4, md: 8 }}>
      <VStack spacing={10} maxW="1200px" mx="auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <VStack textAlign="center" spacing={4}>
            <Heading
              fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
              fontWeight="extrabold"
              bgGradient={`linear(to-r, ${purpleColor}, ${orangeColor})`}
              bgClip="text"
            >
              Our Gallery
            </Heading>
            <Text fontSize="xl" color={textColor} maxW="800px">
              Over 15 Years of Moments of Worship, Wellowship, and Transformation
            </Text>
          </VStack>
        </motion.div>

        <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={6} w="full">
          {galleryImages.map((image, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.03 }}
              transition={{ duration: 0.3 }}
            >
              <Box
                position="relative"
                borderRadius="xl"
                overflow="hidden"
                boxShadow="md"
                cursor="pointer"
                onClick={() => openImage(index)}
              >
                <Image
                  src={image.src}
                  alt={image.caption}
                  objectFit="cover"
                  h="300px"
                  w="100%"
                  transition="all 0.3s ease"
                  _hover={{ transform: 'scale(1.05)' }}
                />
                <Box
                  position="absolute"
                  bottom={0}
                  left={0}
                  right={0}
                  p={4}
                  bgGradient="linear(to-t, blackAlpha.700, transparent)"
                  color="white"
                >
                </Box>
                <Box
                  position="absolute"
                  top={2}
                  right={2}
                  p={2}
                  bg="blackAlpha.600"
                  borderRadius="full"
                >
                  <FaExpand color="white" size="0.8em" />
                </Box>
              </Box>
            </motion.div>
          ))}
        </SimpleGrid>

        {/* Image Modal */}
        <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
          <ModalOverlay />
          <ModalContent bg="transparent" boxShadow="none">
            <ModalCloseButton color="white" _hover={{ color: orangeColor }} />
            <ModalBody p={0} position="relative">
              <HStack spacing={4} justify="center" align="center">
                <IconButton
                  icon={<FaArrowLeft />}
                  aria-label="Previous image"
                  onClick={() => navigateImage('prev')}
                  colorScheme="whiteAlpha"
                  borderRadius="full"
                  size="lg"
                />
                <Image
                  src={galleryImages[selectedImage]?.src}
                  alt={galleryImages[selectedImage]?.caption}
                  maxH="80vh"
                  maxW="100%"
                  objectFit="contain"
                  borderRadius="md"
                />
                <IconButton
                  icon={<FaArrowRight />}
                  aria-label="Next image"
                  onClick={() => navigateImage('next')}
                  colorScheme="whiteAlpha"
                  borderRadius="full"
                  size="lg"
                />
              </HStack>
            </ModalBody>
          </ModalContent>
        </Modal>
      </VStack>
    </Box>
  );
};

export default Gallery;